import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { useSearchParties, type PartyRef } from '@/routes/parties/useParties';

/**
 * Find an existing Person or LegalEntity by name (M1-25) — the picker behind
 * a property party, a relationship link, and the new-customer payer.
 *
 * Same WAI-ARIA combobox-over-listbox shape as `AddressFields`' street-line
 * autocomplete: `role="combobox"` on the input, `role="option"` rows, Up/Down
 * to move, Enter to take, Escape to dismiss. Picking a suggestion is the only
 * way to select — free text alone never resolves to a party, since a
 * property party or a relationship link needs a real id, not a name string.
 */
export function PartyPicker({
  kind,
  onSelect,
  placeholder,
  ariaLabel,
}: {
  kind?: 'Person' | 'LegalEntity';
  onSelect: (party: PartyRef) => void;
  placeholder?: string;
  /** Falls back to the placeholder text's own key — every caller wraps this
   * in something with a visible label already, but the input needs its OWN
   * accessible name since it is not itself inside a `<label>`. */
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const listboxId = `${id}-party-listbox`;
  const optionId = (index: number) => `${id}-party-option-${index}`;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const search = useSearchParties(query, kind);
  const results = search.data ?? [];
  const expanded = open && results.length > 0;

  useEffect(() => {
    if (activeIndex >= results.length) {
      setActiveIndex(results.length - 1);
    }
  }, [activeIndex, results.length]);

  function select(party: PartyRef) {
    onSelect(party);
    setQuery('');
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (results.length > 0) {
        setActiveIndex((current) => (current + 1) % results.length);
      }
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (results.length > 0) {
        setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
      }
      return;
    }
    if (event.key === 'Enter' && expanded && activeIndex >= 0) {
      event.preventDefault();
      select(results[activeIndex]!);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        className={FIELD_CLASS}
        value={query}
        placeholder={placeholder ?? t('partyPicker.placeholder')}
        aria-label={ariaLabel ?? t('partyPicker.placeholder')}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(event.target.value.trim().length > 0);
          setActiveIndex(-1);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setOpen(false);
          setActiveIndex(-1);
        }}
      />
      <ul
        id={listboxId}
        role="listbox"
        aria-label={t('partyPicker.resultsLabel')}
        hidden={!expanded}
        data-testid="party-picker-results"
        className="absolute inset-x-0 z-10 mt-1 max-h-60 overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-background)] py-1 text-sm shadow-md"
      >
        {results.map((party, index) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={party.partyId}
            id={optionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            data-testid="party-picker-option"
            className={cn('cursor-pointer px-3 py-2', index === activeIndex && 'bg-[var(--color-muted)]')}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => select(party)}
          >
            <span className="block font-medium">{party.displayName}</span>
            {party.relationships.length > 0 ? (
              <span className="block text-xs text-[var(--color-muted-foreground)]">
                {party.relationships.map((r) => `${r.roleLabel} of ${r.entityName}`).join(', ')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
